import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Cpu } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Index, Eyebrow, SideImage } from '../components/common/Primitives';
import { growingMethods, controlledEnvironment, statistics } from '../data/site';
import { systemMeta, layerGlance } from '../data/system';
import banner from '../assets/banners/farm-tech.jpg';
import soilChemistry from '../assets/tech/soil-chemistry.jpg';

const FarmTech = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech"
      title="The science behind the freshness"
      lead="Traditional farming is bounded by land, soil fertility, season and weather. Growing indoors, without soil, under precision control removes those limits, and lets one closed-loop system decide what every plant is fed."
    />

    {/* Why it matters. SideImage holds the photo to the text's height; the
        narrower column keeps that crop close to the portrait's own ratio
        instead of forcing it into a wide letterbox. */}
    <Section tone="paper">
      <div className="grid lg:grid-cols-[1fr_22rem] gap-12 items-center">
        <div>
          <Head
            eyebrow="Why farm tech matters"
            title="What growing indoors lets us promise"
            lead="A conventional field cannot promise the same harvest twice, a leaf that was never sprayed, or nutrition that survives the journey. A closed-loop system can."
          />
          <ul className="mt-8 space-y-4">
            {[
              ['Consistent harvests', 'Every tray gets the same light, water and nutrition, so each harvest looks like the last one.'],
              ['Pesticide-free', 'Nothing is sprayed, ever. A crop that shows infection is removed, not treated. No compromise on the health of the consumer.'],
              ['Nutrition intact', 'Harvest to doorstep in about six hours, so what was in the leaf at picking is still there at the table.'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3.5">
                <Check className="w-4.5 h-4.5 text-forest mt-1 shrink-0" />
                <span className="text-[15px] leading-relaxed">
                  <strong className="text-ink font-semibold">{title}.</strong>
                  <span className="text-soil/70"> {body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <SideImage
          src={soilChemistry}
          alt="An agronomist logging pH and nutrient chemistry beside a growing bed"
        />
      </div>
    </Section>

    {/* Stats */}
    <Section tone="ink" tight className="fieldgrid">
      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {statistics.map((s) => (
          <div key={s.label}>
            <dt className="font-display font-semibold text-[2.75rem] md:text-[3.25rem] leading-none text-lime tabular-nums">
              {s.value}
              <span className="text-[1.5rem] align-top ml-0.5 text-lime/70">{s.unit}</span>
            </dt>
            <dd className="mt-3 text-paper/55 text-[14px] leading-snug">{s.label}</dd>
          </div>
        ))}
      </dl>
    </Section>

    {/* Growing methods */}
    <Section tone="paper">
      <Head
        eyebrow="Three methods"
        title="How the food is actually grown"
        lead="Vertical stacking gives us the footprint, hydroponics gives us the water efficiency, and aeroponics gives us the nutrient density. All three run inside the same controlled envelope."
      />

      <div className="mt-14 space-y-8">
        {growingMethods.map((m, i) => (
          <article
            key={m.id}
            id={m.id}
            className="grid lg:grid-cols-[22rem_1fr] gap-8 items-start rounded-2xl bg-chalk ring-1 ring-soil/10 p-6 md:p-8"
          >
            <img
              src={m.image}
              alt={`${m.name} at the Annpriya greenhouse`}
              className="rounded-xl w-full ring-1 ring-soil/10"
            />

            {/* Title leads the text column, so the summary sits under what it
                names and the image column no longer overruns the content. */}
            <div>
              <div className="flex items-center gap-3">
                <Index n={i + 1} />
                <div>
                  <h3 className="font-display font-semibold text-[1.5rem] text-ink leading-none">
                    {m.name}
                  </h3>
                  <p className="label mt-1.5 text-forest">{m.lead}</p>
                </div>
              </div>
              <p className="mt-4 text-soil/70 text-[14.5px] leading-relaxed max-w-3xl">
                {m.summary}
              </p>

              <div className="mt-8 grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <p className="label text-soil/40">How it works</p>
                  <ul className="mt-4 space-y-3">
                    {m.how.map((h) => (
                      <li key={h} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-forest mt-2 shrink-0" />
                        <span className="text-soil/75 text-[14.5px] leading-relaxed">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="label text-soil/40">Benefits</p>
                  <ul className="mt-4 space-y-3">
                    {m.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-forest mt-0.5 shrink-0" />
                        <span className="text-soil/75 text-[14.5px] leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:col-span-2 rounded-xl bg-forest/[0.06] ring-1 ring-forest/15 p-5">
                  <p className="label text-forest">What it means for members</p>
                  <p className="mt-2.5 text-soil/80 text-[14.5px] leading-relaxed">{m.matters}</p>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </Section>

    {/* Controlled environment */}
    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Head
            tone="dark"
            eyebrow="The controlled environment advantage"
            title="One envelope, four variables held steady"
            lead={controlledEnvironment.intro}
          />
          <div className="mt-9 grid sm:grid-cols-2 gap-5">
            {controlledEnvironment.items.map((it) => (
              <div key={it.title}>
                <h3 className="font-display font-semibold text-[1.05rem] text-lime">{it.title}</h3>
                <p className="mt-1.5 text-paper/60 text-[14px] leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-9 text-paper/75 text-[15px] leading-relaxed border-l-2 border-lime pl-5">
            {controlledEnvironment.close}
          </p>
        </div>
        <SideImage
          tone="dark"
          src={controlledEnvironment.image}
          alt="Controlled growing room under tuned LED lighting"
        />
      </div>
    </Section>

    {/* Into the system */}
    <Section tone="paper">
      <div className="rounded-2xl bg-ink text-paper overflow-hidden">
        <div className="p-8 md:p-12">
          <Eyebrow tone="dark">
            <Cpu className="w-3.5 h-3.5" /> The system underneath
          </Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-[2rem] md:text-[2.75rem] leading-[1.12] max-w-3xl">
            {systemMeta.name}
          </h2>
          <p className="mt-5 text-paper/65 text-lg leading-relaxed max-w-2xl">
            Everything above is possible because a six-layer control system measures the soil,
            decides deterministically what it needs, and proves what it actually dispensed.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {layerGlance.map((l) => (
              <div
                key={l.n}
                className={`rounded-lg p-4 ring-1 ${
                  l.accent ? 'bg-amber/10 ring-amber/30' : 'bg-white/[0.04] ring-white/10'
                }`}
              >
                <p className={`label ${l.accent ? 'text-amber' : 'text-lime'}`}>
                  Layer {String(l.n).padStart(2, '0')}
                </p>
                <p className="mt-2 font-medium text-paper text-[15px]">{l.name}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/farm-tech/system"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-ink px-6 py-3 font-semibold hover:bg-paper transition-colors"
            >
              Explore the system <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/farm-tech/architecture"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/20 px-6 py-3 font-medium text-paper hover:ring-lime hover:text-lime transition-colors"
            >
              Layer by layer
            </Link>
          </div>
        </div>
      </div>
    </Section>
  </div>
);

export default FarmTech;
